import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  User, Assessment, AssessmentListItem, AssessmentFormData, AssessmentRun,
  AssessmentReport, AuditLog, UserStats, AIApplication, DpiaTemplate,
  School, SchoolMember, Entitlements, RegistryEntry, RegistryIncident,
  DpoSchool, AppNotification, SchoolDocument, GovernanceDashboard, Delegation,
  MaintenanceState,
} from '@/types'

const BASE_URL = import.meta.env.VITE_API_URL || ''

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Auth is cookie-based (httpOnly): no Authorization header is attached
// client-side. The work session has a hard ceiling (ACCESS_TOKEN_EXPIRE_
// MINUTES, see SessionTimer): it is renewed only by the user's explicit
// click on the expiry warning, never silently. A 401 here therefore always
// means the session has genuinely lapsed (or there never was one) — no
// retry, straight to the login page. Silently refreshing on every 401
// would let an active user's requests keep the session alive forever,
// defeating the point of that warning.
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// ── Auth ───────────────────────────────────────────────────────────────────────

export const authApi = {
  getMe: () => apiClient.get<User>('/auth/me').then((r) => r.data),
  logout: () => apiClient.post('/auth/logout'),
  // Work-session ceiling (see SessionTimer): when the current access token
  // expires. getSession never extends it; renewSession is the one
  // deliberate action that does, issuing a fresh 60-minute token.
  getSession: () =>
    apiClient.get<{ expires_at: string | null }>('/auth/session').then((r) => r.data),
  renewSession: () =>
    apiClient
      .post<{ access_token: string; token_type: string; expires_at: string | null }>('/auth/refresh')
      .then((r) => r.data),
  // P8: revoca tutte le sessioni registrate (refresh token server-side)
  logoutAll: () =>
    apiClient.post<{ revoked: number }>('/auth/logout-all').then((r) => r.data),
  // Configured identity providers (drives the login page buttons).
  getProviders: () =>
    apiClient
      .get<{ google: boolean; microsoft: boolean }>('/auth/providers')
      .then((r) => r.data),
  // `plan`/`next` carry the pricing-page choice through the OAuth round-trip.
  getLoginUrl: (provider: 'google' | 'microsoft', plan?: string, next?: string) => {
    const params = new URLSearchParams()
    if (plan) params.set('plan', plan)
    if (next) params.set('next', next)
    const qs = params.toString()
    return `${BASE_URL}/api/auth/login/${provider}${qs ? `?${qs}` : ''}`
  },
}

// ── Users ──────────────────────────────────────────────────────────────────────

export const usersApi = {
  getStats: () => apiClient.get<UserStats>('/users/stats').then((r) => r.data),
  getEntitlements: () =>
    apiClient.get<Entitlements>('/users/me/entitlements').then((r) => r.data),
  updateProfile: (data: { name?: string; preferred_language?: string }) =>
    apiClient.patch<User>('/users/me', data).then((r) => r.data),
  searchSchools: (q: string) =>
    apiClient.get('/users/schools/search', { params: { q } }).then((r) => r.data),
  // P8: esportazione dei propri dati (Art. 20 GDPR) — download diretto
  exportUrl: () => `${BASE_URL}/api/users/me/export`,
}

// ── Schools (tenant) ───────────────────────────────────────────────────────────

export const schoolsApi = {
  getMine: () => apiClient.get<School>('/users/schools/me').then((r) => r.data),
  leave: () => apiClient.post<User>('/users/schools/leave').then((r) => r.data),
  setDpo: (dpo_email: string | null, dpo_name?: string | null) =>
    apiClient.patch<School>('/users/schools/me/dpo', { dpo_email, dpo_name }).then((r) => r.data),
  dpoSchools: () =>
    apiClient.get<DpoSchool[]>('/users/me/dpo-schools').then((r) => r.data),
  listMembers: () =>
    apiClient.get<SchoolMember[]>('/users/schools/me/members').then((r) => r.data),
  updateMemberRole: (userId: string, role: string) =>
    apiClient.patch<SchoolMember>(`/users/schools/me/members/${userId}`, { role }).then((r) => r.data),
  removeMember: (userId: string) =>
    apiClient.delete(`/users/schools/me/members/${userId}`),
  // P8: il DS conferma il codice ricevuto sulla PEC istituzionale
  verifyPec: (code: string) =>
    apiClient.post<School>('/users/schools/me/verify-pec', { code }).then((r) => r.data),
}

// ── Platform Administration ────────────────────────────────────────────────────

export interface AdminOverview {
  schools: number
  users: number
  active_users: number
  assessments: {
    total: number
    completed: number
    failed: number
    verde: number
    giallo: number
    rosso: number
    vietato: number
    pending_review: number
  }
}

export interface AdminTenant {
  id: string
  name: string
  codice_meccanografico?: string
  city?: string
  province?: string
  email_domain?: string
  created_at: string
  members: number
  assessments: number
  plan: 'FREE' | 'SCHOOL' | 'NETWORK'
}

export type SubscriptionTier = 'SCHOOL' | 'NETWORK'
export type SubscriptionStatus = 'ACTIVE' | 'EXPIRED' | 'SUSPENDED' | 'CANCELLED'
export type BillingPeriod = 'MONTHLY' | 'ANNUAL'

export interface AdminSubscription {
  id: string
  school_id: string
  tier: SubscriptionTier
  status: SubscriptionStatus
  billing_period: BillingPeriod
  valid_from: string
  valid_until: string
  amount?: number | null
  quota?: number | null // null = unlimited
  order_ref?: string | null
  invoice_ref?: string | null
  notes?: string | null
  created_at: string
  is_active: boolean
}

export interface SubscriptionCreate {
  tier: SubscriptionTier
  valid_from: string
  valid_until: string
  billing_period?: BillingPeriod
  amount?: number | null
  quota?: number | null // null = unlimited
  order_ref?: string | null
  invoice_ref?: string | null
  notes?: string | null
}

export interface AdminUser {
  id: string
  name: string
  email: string
  role: string
  school?: string
  is_active: boolean
  created_at: string
}

export interface TenantCreate {
  name: string
  codice_meccanografico?: string
  city?: string
  province?: string
  email_domain?: string
  dirigente_email?: string
}

export interface TenantUpdate {
  name?: string
  codice_meccanografico?: string
  city?: string
  province?: string
  email_domain?: string
  ptof_url?: string
}

export const adminApi = {
  overview: () => apiClient.get<AdminOverview>('/admin/overview').then((r) => r.data),
  listTenants: (params?: { q?: string; page?: number }) =>
    apiClient.get<AdminTenant[]>('/admin/schools', { params }).then((r) => r.data),
  createTenant: (data: TenantCreate) =>
    apiClient.post('/admin/schools', data).then((r) => r.data),
  updateTenant: (schoolId: string, data: TenantUpdate) =>
    apiClient.patch(`/admin/schools/${schoolId}`, data).then((r) => r.data),
  assignDirigenteByEmail: (schoolId: string, email: string) =>
    apiClient.post(`/admin/schools/${schoolId}/dirigente`, { email }).then((r) => r.data),
  // P8: verifica identità Dirigente via IPA + PEC
  ipaLookup: (schoolId: string) =>
    apiClient.post(`/admin/schools/${schoolId}/ipa-lookup`).then((r) => r.data),
  sendPecVerification: (schoolId: string) =>
    apiClient.post(`/admin/schools/${schoolId}/send-pec-verification`).then((r) => r.data),
  listTenantMembers: (schoolId: string) =>
    apiClient.get(`/admin/schools/${schoolId}/members`).then((r) => r.data),
  reassignDirigente: (schoolId: string, userId: string) =>
    apiClient.post(`/admin/schools/${schoolId}/reassign-dirigente`, { user_id: userId }).then((r) => r.data),
  listSubscriptions: (schoolId: string) =>
    apiClient.get<AdminSubscription[]>(`/admin/schools/${schoolId}/subscriptions`).then((r) => r.data),
  createSubscription: (schoolId: string, data: SubscriptionCreate) =>
    apiClient.post<AdminSubscription>(`/admin/schools/${schoolId}/subscriptions`, data).then((r) => r.data),
  setSubscriptionStatus: (subscriptionId: string, status: SubscriptionStatus) =>
    apiClient.patch<AdminSubscription>(`/admin/subscriptions/${subscriptionId}/status`, { status }).then((r) => r.data),
  setSubscriptionQuota: (subscriptionId: string, quota: number | null) =>
    apiClient.patch<AdminSubscription>(`/admin/subscriptions/${subscriptionId}/quota`, { quota }).then((r) => r.data),
  listUsers: (params?: { q?: string; page?: number }) =>
    apiClient.get<AdminUser[]>('/admin/users', { params }).then((r) => r.data),
  setUserStatus: (userId: string, isActive: boolean) =>
    apiClient.patch(`/admin/users/${userId}/status`, { is_active: isActive }).then((r) => r.data),
  gdprErase: (userId: string) =>
    apiClient.post(`/admin/users/${userId}/gdpr-erase`).then((r) => r.data),
  breakGlass: (assessmentId: string, reason: string) =>
    apiClient.post<Assessment>(`/admin/break-glass/assessment/${assessmentId}`, { reason }).then((r) => r.data),
  activateMaintenance: (data: { message: string; reopen_at?: string | null }) =>
    apiClient.post<MaintenanceState>('/admin/maintenance/activate', data).then((r) => r.data),
  deactivateMaintenance: () =>
    apiClient.post<MaintenanceState>('/admin/maintenance/deactivate').then((r) => r.data),
  setMaintenanceNotice: (data: { start: string; end: string; reason: string }) =>
    apiClient.post<MaintenanceState>('/admin/maintenance/notice', data).then((r) => r.data),
  withdrawMaintenanceNotice: () =>
    apiClient.delete<MaintenanceState>('/admin/maintenance/notice').then((r) => r.data),
}

// ── Platform status (public, no auth) ──────────────────────────────────────────

export const systemApi = {
  getMaintenance: () =>
    apiClient.get<MaintenanceState>('/system/maintenance').then((r) => r.data),
}

// ── AI Applications ────────────────────────────────────────────────────────────

export const appsApi = {
  search: (q: string) =>
    apiClient.get<AIApplication[]>('/assessments/apps/search', { params: { q } }).then((r) => r.data),
}

// ── Assessments ────────────────────────────────────────────────────────────────

export const assessmentsApi = {
  list: (params?: {
    page?: number
    page_size?: number
    verdict?: string
    status?: string
    review_status?: string
    chain?: 'dpo_pending' | 'handled'
    search?: string
    scope?: 'mine' | 'school'
    school_id?: string
  }) =>
    apiClient.get<AssessmentListItem[]>('/assessments', { params }).then((r) => r.data),

  create: (data: AssessmentFormData) =>
    apiClient.post<Assessment>('/assessments', data).then((r) => r.data),

  get: (id: string) =>
    apiClient.get<Assessment>(`/assessments/${id}`).then((r) => r.data),

  getStatus: (id: string) =>
    apiClient
      .get<{ id: string; status: string; overall_score?: number; verdict?: string }>(
        `/assessments/${id}/status`
      )
      .then((r) => r.data),

  rerun: (id: string) =>
    apiClient.post(`/assessments/${id}/rerun`).then((r) => r.data),

  runs: (id: string) =>
    apiClient.get<AssessmentRun[]>(`/assessments/${id}/runs`).then((r) => r.data),

  review: (id: string, data: { decision: 'APPROVE' | 'OVERRIDE'; verdict?: string; notes?: string }) =>
    apiClient.post<Assessment>(`/assessments/${id}/review`, data).then((r) => r.data),

  // Revisione DPO della DPIA generata automaticamente (aggiorna solo i campi inviati)
  updateDpia: (id: string, data: Partial<DpiaTemplate>) =>
    apiClient.patch<Assessment>(`/assessments/${id}/dpia`, data).then((r) => r.data),

  registry: () =>
    apiClient.get<RegistryEntry[]>('/assessments/registry').then((r) => r.data),

  // Administrative registry states + incident workflow (P4)
  suspendTool: (appId: string, reason: string, schoolId?: string) =>
    apiClient
      .post<RegistryIncident>(`/assessments/registry/${appId}/suspend`, {
        reason, school_id: schoolId,
      })
      .then((r) => r.data),

  incidentDpoAnalysis: (incidentId: string, notes: string) =>
    apiClient
      .post<RegistryIncident>(`/assessments/registry/incidents/${incidentId}/dpo-analysis`, { notes })
      .then((r) => r.data),

  incidentDecide: (incidentId: string, decision: 'RIPRISTINO' | 'REVOCA', notes?: string) =>
    apiClient
      .post<RegistryIncident>(`/assessments/registry/incidents/${incidentId}/decide`, { decision, notes })
      .then((r) => r.data),

  incidentLift: (incidentId: string, notes: string) =>
    apiClient
      .post<RegistryIncident>(`/assessments/registry/incidents/${incidentId}/lift`, { notes })
      .then((r) => r.data),

  incidents: (params?: { school_id?: string; include_closed?: boolean }) =>
    apiClient
      .get<RegistryIncident[]>('/assessments/registry/incidents', { params })
      .then((r) => r.data),

  // Governance dashboard (P6)
  governance: (schoolId?: string) =>
    apiClient
      .get<GovernanceDashboard>('/assessments/governance', {
        params: schoolId ? { school_id: schoolId } : undefined,
      })
      .then((r) => r.data),
}

// ── Institutional documents (DMS, P5) ──────────────────────────────────────────

export const documentsApi = {
  list: (params?: { app_id?: string; doc_type?: string; group_id?: string; school_id?: string }) =>
    apiClient.get<SchoolDocument[]>('/documents', { params }).then((r) => r.data),

  upload: (data: {
    file: File
    doc_type: string
    title: string
    description?: string
    app_id?: string
    school_id?: string
  }) => {
    const form = new FormData()
    form.append('file', data.file)
    form.append('doc_type', data.doc_type)
    form.append('title', data.title)
    if (data.description) form.append('description', data.description)
    if (data.app_id) form.append('app_id', data.app_id)
    if (data.school_id) form.append('school_id', data.school_id)
    return apiClient
      .post<SchoolDocument>('/documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  newVersion: (documentId: string, file: File, description?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (description) form.append('description', description)
    return apiClient
      .post<SchoolDocument>(`/documents/${documentId}/new-version`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  downloadUrl: (documentId: string) => `${BASE_URL}/api/documents/${documentId}/download`,

  delete: (documentId: string) => apiClient.delete(`/documents/${documentId}`),

  dossierUrl: (appId: string, schoolId?: string) =>
    `${BASE_URL}/api/documents/dossier/${appId}${schoolId ? `?school_id=${schoolId}` : ''}`,
}

// ── Delegations (P7: vicario del Dirigente) ────────────────────────────────────

export const delegationsApi = {
  create: (data: {
    delegato_id: string
    scope_verdetti: boolean
    scope_incidenti: boolean
    scope_notifiche: boolean
    valid_from: string
    valid_until: string
    notes?: string
  }) =>
    apiClient.post<Delegation>('/users/schools/me/delegations', data).then((r) => r.data),

  list: (includeClosed = false) =>
    apiClient
      .get<Delegation[]>('/users/schools/me/delegations', {
        params: { include_closed: includeClosed },
      })
      .then((r) => r.data),

  revoke: (id: string) =>
    apiClient.delete<Delegation>(`/users/schools/me/delegations/${id}`).then((r) => r.data),

  mine: () =>
    apiClient.get<Delegation | null>('/users/me/active-delegation').then((r) => r.data),
}

// ── Notifications ──────────────────────────────────────────────────────────────

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number }) =>
    apiClient.get<AppNotification[]>('/notifications', { params }).then((r) => r.data),

  unreadCount: () =>
    apiClient.get<{ count: number }>('/notifications/unread-count').then((r) => r.data),

  markRead: (id: string) =>
    apiClient.post<AppNotification>(`/notifications/${id}/read`).then((r) => r.data),

  markAllRead: () =>
    apiClient.post<{ marked: number }>('/notifications/read-all').then((r) => r.data),
}

// ── Reports ────────────────────────────────────────────────────────────────────

export const reportsApi = {
  getInfo: (assessmentId: string) =>
    apiClient.get<AssessmentReport>(`/reports/${assessmentId}`).then((r) => r.data),

  downloadUrl: (assessmentId: string) =>
    `${BASE_URL}/api/reports/${assessmentId}/download`,

  regenerate: (assessmentId: string) =>
    apiClient.post(`/reports/${assessmentId}/regenerate`).then((r) => r.data),
}

// ── Audit ──────────────────────────────────────────────────────────────────────

export const auditApi = {
  list: (params?: {
    page?: number
    page_size?: number
    user_id?: string
    action?: string
    date_from?: string
    date_to?: string
  }) => apiClient.get<AuditLog[]>('/audit', { params }).then((r) => r.data),

  count: (params?: { user_id?: string; action?: string }) =>
    apiClient.get<{ count: number }>('/audit/count', { params }).then((r) => r.data),

  exportCsvUrl: (params?: { date_from?: string; date_to?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString()
    return `${BASE_URL}/api/audit/export/csv${qs ? `?${qs}` : ''}`
  },
}
